import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { breadcrumbJsonLd } from "@/lib/breadcrumbs";
import { SERVICES, CITIES, SITE, FBOS } from "@/lib/site-data";
import { CTABand } from "@/components/CTA";
import { CrossLinkModule } from "@/components/CrossLinks";
import { NetworkSuggestion } from "@/components/OurNetwork";
import { Check } from "lucide-react";
import { ldJson, SERVICE_FAQS, faqJsonLd, serviceJsonLd, speakableJsonLd, imageObjectJsonLd } from "@/lib/seo-schema";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { HourlyRates } from "@/components/HourlyRates";
import { pricingFaqJsonLd } from "@/lib/pricing-faq";
import { InstantPriceWidget } from "@/components/InstantPriceWidget";

type ServiceContent = {
  hero: string;
  body: string;
  highlights: string[];
  faqs?: { q: string; a: string }[];
  image?: { src: string; alt: string };
};

// Optimized hero images (1216x640) with descriptive alt text for SEO/accessibility.
const SERVICE_IMAGES: Record<string, { src: string; alt: string }> = {
  "airport-transfers": {
    src: "/og/og-service-airport-transfers.jpg",
    alt: "Black luxury sedan and suited chauffeur at a Bay Area airport arrivals curb at dusk — flight-tracked SFO, SJC, OAK and MRY transfers",
  },
  corporate: {
    src: "/og/og-service-corporate.jpg",
    alt: "Two suited chauffeurs with a black Chevrolet Suburban LT SUV and black Mercedes-Benz Sprinter van outside a Silicon Valley corporate campus — executive ground transportation",
  },
  weddings: {
    src: "/og/og-service-weddings.jpg",
    alt: "Black luxury SUV with wedding ribbon outside a Bay Area vineyard estate at golden hour — chauffeured wedding and special event transportation",
  },
  "wine-tours": {

    src: "/og/og-service-wine-tours.jpg",
    alt: "Female and male chauffeurs in black suits beside a black Lincoln Aviator, black Chevrolet Suburban LT and black Mercedes-Benz Sprinter van on a Napa Valley vineyard drive at golden hour",
  },
  "sprinter-groups": {
    src: "/og/og-service-sprinter-groups.jpg",
    alt: "Black Mercedes-Benz Sprinter executive van with chauffeur outside a Silicon Valley corporate campus — group transportation for 6 to 14 passengers",
  },
  "private-aviation": {
    src: "/og/og-service-private-aviation.jpg",
    alt: "Black Chevrolet Suburban LT SUV on the tarmac beside a private jet at a Bay Area FBO — Premier Executive Chauffeur private aviation ground transportation",
  },
  "sporting-events": {
    src: "/og/og-service-sporting-events.jpg",
    alt: "Black luxury SUV dropping off guests at a Bay Area stadium under the lights — chauffeured transportation for sporting events and concerts",
  },
  "city-tours": {
    src: "/og/og-service-city-tours.jpg",
    alt: "Black luxury SUV crossing the Golden Gate Bridge at sunset — private San Francisco sightseeing and city tour chauffeur service",
  },
};


const CONTENT: Record<string, ServiceContent> = {
  "airport-transfers": {
    hero: "Airport transfers across every major Bay Area airport.",
    body: "We cover SFO, SJC, OAK, and MRY with pre-arranged, flight-tracked chauffeured service. At SJC, sedan and SUV chauffeurs meet passengers inside baggage claim, while Sprinter groups use their terminal's Charter Service pickup booth. At SFO International, the white-striped curbside limousine zone replaced Center Island in August 2026. Other airport meeting points follow the written confirmation.",
    highlights: [
      "SFO — San Francisco International Airport",
      "SJC — San José Mineta International Airport",
      "OAK — Oakland International Airport",
      "MRY — Monterey Regional Airport",
      "SJC sedan and SUV greeting inside baggage claim",
      "SJC Sprinter boarding at the Terminal A or B Charter Service booth",
      "SFO International sedan and SUV pickup at the white-striped curbside zone",
      "Live flight tracking on every arrival",
      "Pre-arranged flat rates — no surge pricing",
    ],
    faqs: [
      { q: "Do you track my flight?", a: "Yes. Every airport pickup is flight-monitored, so a delayed arrival never means a missed pickup." },
      { q: "Where will my chauffeur meet me?", a: "The location depends on the airport and vehicle. SJC sedan and SUV guests are greeted inside baggage claim with a name sign; SJC Sprinter groups use the Charter Service booth at Terminal A or B. SFO International sedan and SUV passengers use the white-striped curbside limousine section by Door 2 or 3 on the A or G side. Your written confirmation provides the final meeting point." },
    ],
  },
  "corporate": {
    hero: "Corporate & executive travel, arranged like clockwork.",
    body: "Corporate travel runs like clockwork when every executive detail is anticipated. Silicon Valley executive assistants trust our corporate chauffeur team because we get the small things right — chauffeur names shared in advance, confirmations sent to the right inboxes, discreet on-site behavior at executive offices, and consistent corporate pricing. Open a corporate account for consolidated billing, priority dispatch, and dedicated executive support around the clock.",
    highlights: [
      "Consolidated monthly billing for corporate accounts",
      "Priority dispatch across the Bay Area",
      "Dedicated executive account manager",
      "Pre-arranged flat rates for point-to-point and hourly travel",
      "live dispatch desk — never voicemail",
    ],
  },
  "private-aviation": {
    hero: "FBO curbside service for private aviation.",
    body: "We provide direct FBO-to-address and address-to-FBO chauffeur transfers across every major Bay Area FBO. Our chauffeurs are familiar with each ramp's protocols, so you never wait — and neither does your crew.",
    highlights: FBOS.map((f) => f.name),
  },
  "sprinter-groups": {
    hero: "Mercedes-Benz Sprinter for 6–14 passenger groups.",
    body: "The executive Sprinter is the workhorse of Silicon Valley group travel — a boardroom-quality cabin on wheels for corporate roadshows, executive offsites, investor days, wedding parties, wine-country outings, and family reunions. Every Sprinter in our fleet is black-on-black with captain's seating, USB power at every seat, climate-zoned rear cabin, and a rear luggage bay sized for full airline carry-ons and checked bags. One chauffeur stays with your group for the entire day, so there are no handoffs, no lost bags, and no waiting on a second vehicle to catch up. Whether you're moving a leadership team between Sand Hill Road and downtown San Francisco, shuttling a wedding party between hotel and vineyard, or running a multi-city investor roadshow across the Peninsula, the Sprinter keeps everyone together and on schedule.",
    highlights: [
      "6–14 passenger capacity with captain's seating configurations",
      "USB-C power at every seat and rear-cabin climate control",
      "Onboard Wi-Fi option for corporate roadshows and offsites",
      "Rear luggage bay sized for full airline bag loads",
      "Silicon Valley corporate roadshows and investor days",
      "Napa, Sonoma, Livermore, and Santa Cruz Mountains wine tours",
      "Wedding-party and bachelor/bachelorette group transfers",
      "SFO, SJC, OAK, and MRY airport group transfers",
      "Single chauffeur stays with your group for the entire itinerary",
      "staffed dispatch — schedule changes handled live, never through voicemail",
    ],
  },
  "wine-tours": {
    hero: "Napa & Sonoma wine tours, handled end to end.",
    body: "A great wine day starts before the first pour. We build the itinerary around your party's preferences — reds versus whites, boutique estates versus flagship houses, food pairings, private caves, library tastings — then book the appointments, coordinate arrival windows with each winery, and chauffeur your group door to door from Silicon Valley, San Francisco, or your Napa hotel. Full-day itineraries typically cover three to four wineries with a chef-driven lunch stop; half-day options are ideal for shorter windows or wine-club pickup runs. Vehicles are staged with a chilled cooler for your purchases, bottled water, and space for cases in the rear bay. Chevrolet Suburban LT seats up to six with luggage, and the executive Sprinter carries the full party of up to fourteen. Your chauffeur handles the driving, the estate check-ins, and the paperwork on any club shipments — you handle the tasting notes.",
    highlights: [
      "Napa Valley, Sonoma County, and Carneros AVA vineyards",
      "Livermore Valley and Santa Cruz Mountains wine trails",
      "Concierge team pre-books tastings, tours, and lunches",
      "Full-day (8–10 hours) and half-day itineraries",
      "Pickups from Bay Area home, hotel, or vineyard estate",
      "Chilled cooler and case storage for wine-club purchases",
      "Suburban LT (up to 6) or executive Sprinter (up to 14)",
      "Private-cave, library, and vertical tasting reservations on request",
      "Flat pre-arranged rate — tolls, parking, and gratuity itemized on invoice",
    ],
  },
  "weddings": {
    hero: "Weddings, proms, and milestone events.",
    body: "The most important days of your life deserve a chauffeur team that has done this before — quietly, reliably, and without drama. Bay Area weddings run on tight timelines across multiple venues, and our wedding coordinators build a minute-by-minute run of show for every vehicle: bride and groom transport, wedding-party Sprinter, family sedans, and guest shuttle rotations between ceremony, reception, and hotel blocks. Chauffeurs arrive in black suits with white gloves on request, vehicles are detailed the morning of the event, and complimentary white ribbon presentation is available for the bridal car. A single point of contact stays on dispatch throughout the day, so a delayed ceremony, a weather change, or an unexpected after-party is handled in real time. Peak wedding season in the Bay Area fills four to six months out — book as soon as your venue is confirmed.",
    highlights: [
      "Bride, groom, and immediate-family transfers",
      "Wedding-party Sprinter service (up to 14 passengers)",
      "Multi-vehicle guest shuttle rotations between venues and hotels",
      "Complimentary white ribbon presentation for the bridal car",
      "Proms, quinceañeras, and sweet-sixteen packages",
      "Milestone birthdays, anniversaries, and vow renewals",
      "Bachelor and bachelorette wine-country and city itineraries",
      "Single dedicated coordinator on dispatch throughout the event",
      "Vehicles detailed the morning of the wedding — no exceptions",
      "Napa, Sonoma, Half Moon Bay, Carmel, and Big Sur destination weddings",
    ],
  },
  "sporting-events": {
    hero: "Sporting events & concerts, without the parking headache.",
    body: "Bay Area game days and stadium concerts turn into two-hour traffic exercises before the first pitch or downbeat — unless someone else is driving. We know the drop-off protocols, VIP entrances, rideshare cutoffs, and post-event pickup logistics for every major venue from Levi's Stadium down to Shoreline Amphitheatre. For corporate suites and hospitality boxes, we coordinate multi-vehicle group rides so your guests arrive together and leave together. Chauffeurs stay on standby during the event and reposition to the pre-arranged pickup spot the moment the fourth quarter ends or the encore starts, so you're not standing in a parking lot flagging down a rideshare with 30,000 other fans. Standing orders for full seasons (49ers, Warriors, Giants, Sharks) are welcome and priced at preferred hourly rates.",
    highlights: [
      "Levi's Stadium — 49ers, Earthquakes, international soccer, stadium concerts",
      "SAP Center — Sharks games and touring concerts",
      "Chase Center — Warriors games and arena concerts",
      "Oracle Park — Giants games and ballpark concerts",
      "Shoreline Amphitheatre — outdoor concert season",
      "Frost Amphitheater, The Fillmore, and Fox Theater Oakland",
      "Corporate suite and hospitality-box group coordination",
      "Chauffeur stays on standby — no waiting for a return rideshare",
      "Season standing orders for 49ers, Warriors, Giants, and Sharks",
      "Pre-arranged drop-off at VIP entrances where credentialed",
    ],
  },
  "city-tours": {
    hero: "City tours and sightseeing across Northern California.",
    body: "The best way to see San Francisco, the Monterey Peninsula, Santa Cruz, and Napa is from the back of a black Suburban with a chauffeur who lives here. Every tour is private and fully custom — we plan around your party's pace, photo stops, food interests, and physical mobility. Half-day San Francisco tours cover the classic landmarks (Golden Gate, Presidio, Painted Ladies, Ferry Building, Chinatown) with local coffee and lunch recommendations built in. Full-day itineraries stretch down the coast to Half Moon Bay, over the hill to Santa Cruz, or south to Monterey and Carmel for 17-Mile Drive and Cannery Row. Napa and Sonoma day tours pair vineyard visits with downtown walking and the Oxbow Public Market. Chauffeurs share local context along the way — history, architecture, current events — but never lecture. This is the version of the city and coast that most visitors, and plenty of locals, never see.",
    highlights: [
      "San Francisco: Golden Gate, Presidio, Painted Ladies, Ferry Building, Chinatown",
      "Coastal: Half Moon Bay, Pescadero, Santa Cruz beaches and redwoods",
      "Monterey & Carmel: 17-Mile Drive, Pebble Beach, Cannery Row, Carmel Village",
      "Napa Valley: vineyards, downtown Napa, Oxbow Public Market",
      "Sonoma County: Healdsburg square, Russian River, Sonoma Plaza",
      "Half-day, full-day, and multi-day itineraries",
      "Private, fully custom — no group tour buses, no fixed script",
      "Family- and mobility-friendly pacing on request",
      "Chauffeur-narrated with local history and current recommendations",
      "Suburban LT (up to 6) or executive Sprinter (up to 14)",
    ],
  },
};

export const Route = createFileRoute("/services/$service")({
  loader: ({ params }) => {
    const svc = SERVICES.find((s) => s.slug === params.service);
    const content = CONTENT[params.service];
    if (!svc || !content) throw notFound();
    return { svc, content };
  },
  head: ({ params, loaderData }) => {
    const title = loaderData?.svc.title ?? "Service";
    const desc = loaderData?.content.body.slice(0, 155) ?? "";
    const url = `https://premierexecutivechauffeur.com/services/${params.service}`;
    const img = SERVICE_IMAGES[params.service];
    const imgAbs = img ? `https://premierexecutivechauffeur.com${img.src}` : undefined;
    const svcFaqs = [
      ...(loaderData?.content.faqs ?? []),
      ...(SERVICE_FAQS[params.service] ?? []),
    ];
    const dedupedFaqs = Array.from(new Map(svcFaqs.map((f) => [f.q, f])).values());
    const cityNames = CITIES.slice(0, 12).map((c) => c.name);
    return {
      meta: [
        { title: `${title} — Premier Chauffeur Bay Area` },
        { name: "description", content: desc },
        { property: "og:title", content: `${title} | Premier Executive Chauffeur` },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        ...(imgAbs
          ? [
              { property: "og:image", content: imgAbs },
              { property: "og:image:alt", content: img!.alt },
              { property: "og:image:width", content: "1216" },
              { property: "og:image:height", content: "640" },
              { name: "twitter:card", content: "summary_large_image" },
              { name: "twitter:image", content: imgAbs },
              { name: "twitter:image:alt", content: img!.alt },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json" as const, children: ldJson(pricingFaqJsonLd(url)) },
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: title, path: url },
        ]),
        {
          type: "application/ld+json",
          children: ldJson({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${url}#webpage`,
            url,
            name: `${title} | Premier Executive Chauffeur`,
            description: desc,
            inLanguage: "en-US",
            isPartOf: { "@id": "https://premierexecutivechauffeur.com#website" },
            about: { "@id": `${url}#service` },
            breadcrumb: { "@id": `${url}#breadcrumb` },
            ...(imgAbs ? { primaryImageOfPage: { "@id": `${url}#hero-image` } } : {}),
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2"] },
          }),
        },
        ...(imgAbs
          ? [{
              type: "application/ld+json" as const,
              children: ldJson({
                "@context": "https://schema.org",
                "@type": "ImageObject",
                creator: { "@type": "Organization", name: "Premier Executive Chauffeur", url: "https://premierexecutivechauffeur.com" },
                creditText: "Premier Executive Chauffeur",
                copyrightNotice: "© Premier Executive Chauffeur",
                copyrightHolder: { "@type": "Organization", name: "Premier Executive Chauffeur", url: "https://premierexecutivechauffeur.com" },
                license: "https://premierexecutivechauffeur.com/terms",
                acquireLicensePage: "https://premierexecutivechauffeur.com/contact",
                "@id": `${url}#hero-image`,
                url: imgAbs,
                contentUrl: imgAbs,
                caption: img!.alt,
                representativeOfPage: true,
              }),
            }]
          : []),
        {
          type: "application/ld+json",
          children: ldJson(
            serviceJsonLd({
              name: title,
              description: desc,
              url: `/services/${params.service}`,
              serviceType: title,
              areaServedCities: cityNames,
              ...(imgAbs ? { image: imageObjectJsonLd({ url: imgAbs, contentUrl: imgAbs, caption: img!.alt, description: img!.alt }) } : {}),
            }),

          ),
        },
        ...(dedupedFaqs.length
          ? [{ type: "application/ld+json", children: ldJson(faqJsonLd(dedupedFaqs)) }]
          : []),
        { type: "application/ld+json", children: ldJson(speakableJsonLd(url)) },
      ],

    };
  },
  component: ServiceDetail,
  notFoundComponent: () => (
    <div className="container-x py-24 text-center">
      <h1 className="text-3xl mb-4">Service not found</h1>
      <Link to="/services" className="text-gold underline underline-offset-2">Back to services</Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container-x py-24 text-center">
      <h1 className="text-3xl mb-4">Something went wrong</h1>
      <p className="text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
});

function ServiceDetail() {
  const { svc, content } = Route.useLoaderData();
  const heroImage = SERVICE_IMAGES[svc.slug];
  const sharedFaqs = SERVICE_FAQS[svc.slug] ?? [];
  const combinedFaqs = Array.from(
    new Map([...(content.faqs ?? []), ...sharedFaqs].map((f) => [f.q, f])).values(),
  );
  return (
    <>
      <section className="container-x py-20 md:py-24 max-w-6xl">
        <Breadcrumbs
          items={[
            { name: "Home", to: "/" },
            { name: "Services", to: "/services" },
            { name: svc.title },
          ]}
          className="mb-4"
        />
        <div className={heroImage ? "grid gap-10 lg:grid-cols-[1.05fr_1fr] items-center" : ""}>
          <div>
            <h1 className="text-4xl md:text-5xl mb-5">{content.hero}</h1>
            <p className="text-lg text-muted-foreground speakable">{content.body}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={SITE.bookUrl} target="_blank" rel="noopener noreferrer" className="btn-gold hover:btn-gold-hover">Reserve now</a>
              <a href={SITE.corporateUrl} target="_blank" rel="noopener noreferrer" className="btn-outline hover:bg-gold hover:text-primary-foreground">Corporate account</a>
            </div>
          </div>
          {heroImage && (
            <figure className="border border-border overflow-hidden bg-surface">
              <picture>
                <source type="image/avif" srcSet={heroImage.src.replace(/\.jpg$/, ".avif")} />
                <source type="image/webp" srcSet={heroImage.src.replace(/\.jpg$/, ".webp")} />
                <img
                  src={heroImage.src}
                  alt={heroImage.alt}
                  width={1216}
                  height={640}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-auto block"
                />
              </picture>
            </figure>
          )}
        </div>
      </section>


      <section className="container-x pb-20">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <h2 className="text-2xl mb-6">What's included</h2>
            <ul className="space-y-3">
              {content.highlights.map((h: string) => (
                <li key={h} className="flex gap-3 items-start text-sm text-muted-foreground border-b border-border pb-3">
                  <Check className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" /> <span>{h}</span>
                </li>
              ))}
            </ul>
            {combinedFaqs.length > 0 && (
              <div className="mt-12 speakable">
                <h2 className="text-2xl mb-6">Frequently asked</h2>
                <div className="space-y-4">
                  {combinedFaqs.map((f) => (
                    <details key={f.q} className="border border-border p-5 group">
                      <summary className="cursor-pointer text-foreground group-open:text-gold">{f.q}</summary>
                      <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </div>
          <aside className="border border-border p-6 h-fit sticky top-28">
            <p className="eyebrow mb-4">Coverage</p>
            <p className="text-sm text-muted-foreground">
              Every major Bay Area airport (SFO, SJC, OAK, MRY) and every FBO on the peninsula, plus door-to-door service across Silicon Valley, San Francisco, the East Bay, Santa Cruz County, and the Monterey Peninsula.
            </p>
            <div className="gold-rule my-6" />
            <p className="eyebrow mb-3">Hours</p>
            <p className="text-sm text-muted-foreground">live dispatch desk — every day of the year.</p>
          </aside>
        </div>
      </section>

      <InstantPriceWidget
        heading={`Instant price for ${svc.title.toLowerCase()}`}
        intro={`Enter your pickup and drop-off for a flat all-in ${svc.title.toLowerCase()} quote. One-way point-to-point and city-to-city transfers carry no hourly minimum.`}
      />

      <HourlyRates
        heading={`Hourly rates for ${svc.title.toLowerCase()}`}
        intro={`Transparent published hourly pricing applies to ${svc.title.toLowerCase()} across Silicon Valley and the Bay Area. Five-hour minimum on all as-directed bookings; tolls, parking, airport fees, and gratuity are quoted up front.`}
      />


      <section className="container-x py-8">
        <NetworkSuggestion
          context={
            svc.slug === "corporate"
              ? { type: "corporate" }
              : { type: "service", slug: svc.slug }
          }
        />
      </section>


      <CrossLinkModule context={{ type: "service", slug: svc.slug }} />

      <CTABand heading={`Reserve ${svc.title.toLowerCase()} in under two minutes`} sub="Live dispatchers available daily for last-minute changes." />
    </>
  );
}
